import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  Cpu,
  Database,
  BarChart3,
  ServerCog,
  BellRing,
  Settings,
  Building,
  ChevronRight
} from 'lucide-react';
import { cn } from '../../utils/cn'
import { AnimatePresence, motion } from 'framer-motion';

interface MenuItem {
  id: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
  section?: string;
  requiresAdmin?: boolean;
}

const menuItems: MenuItem[] = [
  {
    id: 'ai-mcp',
    label: 'AI/MCP',
    path: '/ai-mcp',
    icon: Cpu,
    description: 'AI agents and MCP protocol management',
    section: 'Operations',
  },
  {
    id: 'consensus',
    label: 'Consensus',
    path: '/consensus',
    icon: Database,
    description: 'Raft consensus protocol monitoring',
    section: 'Operations',
  },
  {
    id: 'crdt',
    label: 'CRDT Operations',
    path: '/crdt',
    icon: BarChart3,
    description: 'Conflict-free replicated data types',
    section: 'Operations',
  },
  {
    id: 'storage',
    label: 'Storage',
    path: '/storage',
    icon: ServerCog,
    description: 'Distributed storage management',
    section: 'System',
  },
  {
    id: 'alerts',
    label: 'Alerts',
    path: '/alerts',
    icon: BellRing,
    description: 'Alert rules and notifications',
    section: 'System',
  },
  {
    id: 'config',
    label: 'Configuration',
    path: '/config',
    icon: Settings,
    description: 'System configuration and settings',
    section: 'System',
  },
  {
    id: 'platform',
    label: 'Platform Overview',
    path: '/admin/platform',
    icon: Building,
    description: 'Platform administration dashboard',
    section: 'Admin',
    requiresAdmin: true,
  },
];

interface MoreMenuSheetProps {
  isOpen: boolean;
  onClose: () => void;
  userRole?: string;
}

export function MoreMenuSheet({ isOpen, onClose, userRole = 'user' }: MoreMenuSheetProps) {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Filter menu items based on user role
  const filteredItems = menuItems.filter(
    item => !item.requiresAdmin || userRole === 'admin'
  );

  // Group items by section
  const groupedItems = filteredItems.reduce((acc, item) => {
    const section = item.section || 'Other';
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {} as Record<string, MenuItem[]>);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={onClose}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ 
              type: 'spring',
              damping: 30,
              stiffness: 300
            }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900 border-t border-gray-800 rounded-t-2xl lg:hidden safe-area-bottom"
          >
            {/* Handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-12 h-1 bg-gray-600 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-800">
              <h2 className="text-lg font-semibold text-white">More Options</h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="overflow-y-auto max-h-[60vh] px-4 py-2">
              {Object.entries(groupedItems).map(([section, items]) => (
                <div key={section} className="mb-4">
                  <h3 className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {section}
                  </h3>
                  <div className="space-y-1">
                    {items.map((item) => (
                      <Link
                        key={item.id}
                        to={item.path}
                        onClick={onClose}
                        className={cn(
                          "flex items-center justify-between px-4 py-3 rounded-lg",
                          "hover:bg-gray-800 transition-colors",
                          "touch-target focus:outline-none focus:ring-2 focus:ring-blue-500"
                        )}
                      >
                        <div className="flex items-center space-x-3">
                          <item.icon className="w-5 h-5 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-white">
                              {item.label}
                            </div>
                            {item.description && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                {item.description}
                              </div>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom padding for gesture area */}
            <div className="h-8" />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}